import type { CatalogSortOrder } from "../components/catalog-sort-control.tsx"
import type { MyLegalDocuments } from "../components/my-legal-dialogs.tsx"
import type { UserView } from "./user-view.ts"

type CatalogSessionFilters = { affordableOnly: boolean; brand: string; query: string; sort: CatalogSortOrder }

export type ConsumerSessionRenderState = { readonly visible: boolean; readonly generation: number; readonly epoch: number }

export const nextConsumerSessionRenderState = (state: ConsumerSessionRenderState, kind: "clearing" | "ready", epoch: number, detail: unknown): ConsumerSessionRenderState => {
  const reason = typeof detail === "object" && detail !== null && "reason" in detail ? detail.reason : undefined
  // Keep the logout control mounted until its request can report a failure.
  if ((kind === "clearing" && reason === "logout") || (kind === "ready" && reason === "logout-failed")) return { ...state, epoch }
  return { visible: kind === "ready", generation: epoch, epoch }
}

const initialCatalogFilters = (): CatalogSessionFilters => ({ affordableOnly: false, brand: "all", query: "", sort: "recommended" })

const canRememberView = (key: string): boolean => {
  const pathname = key.split(/[?#]/u)[0]
  return pathname === "/home" || pathname === "/shop" || pathname === "/shop/search" || pathname === "/my"
}

export class ConsumerSessionMemory {
  #epoch = 0
  readonly #views = new Map<string, UserView>()
  readonly scroll = new Map<string, number>()
  catalog = initialCatalogFilters()
  legalDocuments: MyLegalDocuments | undefined

  get epoch(): number {
    return this.#epoch
  }

  readView(key: string): UserView | undefined {
    return canRememberView(key) ? this.#views.get(key) : undefined
  }

  writeView(key: string, view: UserView, epoch: number): void {
    if (epoch !== this.#epoch || !canRememberView(key)) return
    this.#views.delete(key)
    this.#views.set(key, view)
    if (this.#views.size > 16) {
      const oldest = this.#views.keys().next().value
      if (oldest !== undefined) this.#views.delete(oldest)
    }
  }

  clear(): void {
    this.#epoch += 1
    this.#views.clear()
    this.catalog = initialCatalogFilters()
    this.scroll.clear()
    this.legalDocuments = undefined
  }
}
